import { addBusinessDays, startOfToday } from "date-fns"

import { isBrowser } from "@p-ban/common/src/utils/CommonUtils"

import scheduleApi from "../../../api/schedule/scheduleApi"
import ESTIMATE_CONSTANT from "../constant/estimateConstant"
import getNumberOfMinPattern from "../pcb/service/getNumberOfMinPattern"
import type EstimateValues from "../types/params"
import getNumberOfLayers from "./getNumberOfLayers"
import { isAluminum, isCopper, isFlexible, isMEGTRON6, isMetal, isRigid, isRogers } from "./oneClickIs"

const EMPTY = ""
const NO_SELECT = ""

const createMessage = (values: Partial<EstimateValues>) => {
  return `初期値の取得に失敗しました。${JSON.stringify(values)}`
}

const get接着面 = ({ kibanType, layer }: Pick<EstimateValues, "kibanType" | "layer">) => {
  if (!isFlexible({ kibanType })) {
    return EMPTY
  }

  switch (getNumberOfLayers({ layer })) {
    case 1:
      return ESTIMATE_CONSTANT.接着面["パターン面と同じ面(L1面)(片面)"].value
    case 2:
      return ESTIMATE_CONSTANT.接着面["パターン面と同じ面(L1面)(2層)"].value
    case 3:
      return ESTIMATE_CONSTANT.接着面["パターン面と同じ面(L1面)(3層)"].value
    case 4:
      return ESTIMATE_CONSTANT.接着面["パターン面と同じ面(L1面)(4層)"].value
    default:
      throw new Error(createMessage({ kibanType, layer }))
  }
}

let initialDate: Date | null = null
if (isBrowser) {
  const init = async () => {
    const result = await scheduleApi.receivableDates()
    if (result.isSuccess) {
      initialDate = result.data[0] ?? null
    }
  }

  // 開発環境だとmswの起動前にリクエストが送られてしまうため、API呼び出しを遅延させてます。
  setTimeout(init, 0)
}

const getInitialValue = {
  kibanType: () => ESTIMATE_CONSTANT.基板の種類.リジッド基板.value,

  serviceD: () => false,
  serviceM: () => true,
  serviceG: () => false,
  partsProcurementCourse: () => false,

  metalBoard: () => ESTIMATE_CONSTANT.メタル材質.アルミ.value,
  layer({ kibanType, metalBoard }: Pick<EstimateValues, "kibanType" | "metalBoard">) {
    if (isRigid({ kibanType })) return ESTIMATE_CONSTANT.構成層数["2層"].value
    if (isFlexible({ kibanType })) return ESTIMATE_CONSTANT.構成層数.FPC2層両面.value
    if (isMetal({ kibanType })) {
      if (isAluminum({ metalBoard })) return ESTIMATE_CONSTANT.構成層数.片面アルミベース.value
      if (isCopper({ metalBoard })) return ESTIMATE_CONSTANT.構成層数.片面銅ベース.value
    }

    throw new Error(createMessage({ kibanType, metalBoard }))
  },
  patternSide({ kibanType }: Pick<EstimateValues, "kibanType">) {
    if (isRigid({ kibanType })) return ESTIMATE_CONSTANT.パターン面.L1面視のL2面.value
    if (isFlexible({ kibanType })) return ESTIMATE_CONSTANT.パターン面.L1面視のL1面.value
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT.パターン面.L1面視のL1面.value

    throw new Error(createMessage({ kibanType }))
  },
  sizeX: () => null,
  sizeY: () => null,
  fpcRectangularShape: () => ESTIMATE_CONSTANT.矩形形状.はい.value,

  pinCnt: () => null,
  planCadDataKbn: () => ESTIMATE_CONSTANT.CAD選択.CAD指定なし.value,
  fileKbn: () => ESTIMATE_CONSTANT.回路図の支給形態["回路図＋ネットリスト"].value,
  designInstruction: () => ESTIMATE_CONSTANT["固有の仕様書・設計指示書"].なし.value,

  cnt: () => null,

  sheetCnt: () => null,
  solderType: () => ESTIMATE_CONSTANT.半田種類.共晶半田.value,
  pieceCnt: () => null,
  bgacsp: () => null,
  sideOfSmd: () => NO_SELECT,
  dipPartCnt: () => null,
  sideOfDip: () => NO_SELECT,
  provisionOfMountData: () => NO_SELECT,
  metalmask: () => ESTIMATE_CONSTANT.実装方法.リフロー実装をする.value,
  metalMaskDtl({ serviceD, serviceG }: Pick<EstimateValues, "serviceD" | "serviceG">) {
    // 仕様では設計・製造・実装、全て選択された場合、"マスク用ガーバーデータから製造する"を初期値に変更するとしています。
    // しかし以下の理由から製造は判定条件に含めず、設計・実装が選択された場合に初期値を変更します。
    // ・製造なしで設計+実装という注文は出来ないので、設計と実装が選択されたら設計・製造・実装が選択されたとみなしても実害がない。
    // ・実装のみで"マスク用ガーバーデータが無く～"の選択肢が選択された状態で、設計を選択すると"マスク用ガーバーデータが無く～"の選択肢がなくなり初期値が設定されます。
    // 　この際製造を判定処理に含めていると、未選択の状態になり選択肢を変更したことに関する通知処理がスキップされてしまうため。
    return serviceD && serviceG
      ? ESTIMATE_CONSTANT["メタルマスクの製造・支給選択"].マスク用ガーバーデータから製造する.value
      : NO_SELECT
  },
  freeMaterialsFlg: () => ESTIMATE_CONSTANT["P板.com無料提供部品"].あり.value,
  userProvPartFlg: () => ESTIMATE_CONSTANT.お客様支給部品.あり.value,
  materialsArrivalDate() {
    return initialDate ?? addBusinessDays(startOfToday(), 1)
  },

  board({ kibanType, layer }: Pick<EstimateValues, "kibanType" | "layer">) {
    if (isRigid({ kibanType })) return ESTIMATE_CONSTANT.材質["FR-4"].value
    if (isFlexible({ kibanType })) {
      switch (getNumberOfLayers({ layer })) {
        case 1:
        case 2:
          return ESTIMATE_CONSTANT.材質["銅箔＋接着層＋PI（ポリイミド）"].value
        case 3:
        case 4:
          return ESTIMATE_CONSTANT.材質["銅箔＋PI（ポリイミド）"].value
      }
    }
    if (isMetal({ kibanType })) return ""

    throw new Error(createMessage({ kibanType, layer }))
  },
  boardThickness({ layer, board }: Pick<EstimateValues, "layer" | "board">) {
    if (isRogers({ board })) return ESTIMATE_CONSTANT.板厚["0.25mm（Rogers材）"].value
    if (isMEGTRON6({ board })) {
      const numberOfLayers = getNumberOfLayers({ layer })
      if (numberOfLayers === 2) return ESTIMATE_CONSTANT.板厚["0.8mm"].value
      if (numberOfLayers >= 4) return ESTIMATE_CONSTANT.板厚["1.2mm"].value
    }
    return ESTIMATE_CONSTANT.板厚["1.6mm"].value
  },
  fpcBoardThickness({ kibanType, layer, minPattern }: Pick<EstimateValues, "kibanType" | "layer" | "minPattern">) {
    if (!isFlexible({ kibanType })) {
      return EMPTY
    }

    switch (getNumberOfLayers({ layer })) {
      case 1:
        return getNumberOfMinPattern({ minPattern }) < 0.1
          ? ESTIMATE_CONSTANT.ベース材厚["25μm(0.1mm未満)"].value
          : ESTIMATE_CONSTANT.ベース材厚["25μm(0.1mm以上__片面)"].value
      case 2:
        return getNumberOfMinPattern({ minPattern }) < 0.1
          ? ESTIMATE_CONSTANT.ベース材厚["12.5μm(0.1mm未満)"].value
          : ESTIMATE_CONSTANT.ベース材厚["12.5μm(0.1mm以上__2層)"].value
      case 3:
        return ESTIMATE_CONSTANT.ベース材厚["12.5μm(3層)"].value
      case 4:
        return ESTIMATE_CONSTANT.ベース材厚["12.5μm(4層)"].value
    }

    throw new Error(createMessage({ kibanType, layer, minPattern }))
  },
  almThickness({ metalBoard }: Pick<EstimateValues, "metalBoard">) {
    if (isAluminum({ metalBoard })) return ESTIMATE_CONSTANT["ベース／コアの厚み"]["1.5mm"].value
    if (isCopper({ metalBoard })) return ESTIMATE_CONSTANT["ベース／コアの厚み"]["1.0mm"].value

    throw new Error(createMessage({ metalBoard }))
  },
  beatenThickness({ kibanType, layer, minPattern }: Pick<EstimateValues, "kibanType" | "layer" | "minPattern">) {
    if (isRigid({ kibanType })) {
      const numberOfLayers = getNumberOfLayers({ layer })
      if (numberOfLayers <= 2) return ESTIMATE_CONSTANT.銅箔厚み["18μm(リジッド)"].value
      if (numberOfLayers >= 4) return ESTIMATE_CONSTANT.銅箔厚み["内層35μm外層18μm"].value
    }
    if (isFlexible({ kibanType })) {
      switch (getNumberOfLayers({ layer })) {
        case 1:
          return ESTIMATE_CONSTANT.銅箔厚み["35μm(フレキシブル__片面)"].value
        case 2:
          return getNumberOfMinPattern({ minPattern }) < 0.1
            ? ESTIMATE_CONSTANT.銅箔厚み["18μm(フレキシブル__0.1mm未満)"].value
            : ESTIMATE_CONSTANT.銅箔厚み["18μm(フレキシブル__0.1mm以上__2層)"].value
        case 3:
        case 4:
          return ESTIMATE_CONSTANT.銅箔厚み["内層18μm外層18μm(フレキシブル)"].value
      }
    }
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT.銅箔厚み["35μm(メタル)"].value

    throw new Error(createMessage({ kibanType, layer, minPattern }))
  },
  buildUpMethod({ layer }: Pick<EstimateValues, "layer">) {
    switch (getNumberOfLayers({ layer })) {
      case 4:
        return ESTIMATE_CONSTANT.ビルドアップ工法["なし(4層)"].value
      case 6:
        return ESTIMATE_CONSTANT.ビルドアップ工法["なし(6層)"].value
      case 8:
        return ESTIMATE_CONSTANT.ビルドアップ工法["なし(8層)"].value
      case 10:
        return ESTIMATE_CONSTANT.ビルドアップ工法["なし(10層)"].value
      case 12:
        return ESTIMATE_CONSTANT.ビルドアップ工法["なし(12層)"].value
      default:
        return ESTIMATE_CONSTANT.ビルドアップ工法["なし(片面2層)"].value
    }
  },

  minPattern: () => ESTIMATE_CONSTANT["最小パターン幅／間隔"]["0.127mm"].value,
  minLand({ kibanType }: Pick<EstimateValues, "kibanType">) {
    if (isRigid({ kibanType })) return ESTIMATE_CONSTANT["最小ビア径／ランド径"]["φ0.3/0.6mm(リジッド)"].value
    if (isFlexible({ kibanType })) return ESTIMATE_CONSTANT["最小ビア径／ランド径"]["φ0.3/0.6mm(フレキシブル)"].value
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT["最小ビア径／ランド径"]["φ0.3/0.6mm(メタル)"].value

    throw new Error(createMessage({ kibanType }))
  },
  laserVia: () => ESTIMATE_CONSTANT["レーザービア径／ランド径"]["φ0.1/0.3mm"].value,
  externalsSilk: () => ESTIMATE_CONSTANT["外形が特殊形状・カードエッジコネクタ"].なし.value,
  controlWiring: () => ESTIMATE_CONSTANT["配置／配線（配線幅・配線長・シールド）ルール"].なし.value,
  circuit: () => ESTIMATE_CONSTANT["高電流（10A以上）高電圧（50V以上）多電源（4種以上）"].なし.value,
  planBgacsp: () => ESTIMATE_CONSTANT["BGA／CSP"].なし.value,
  otherSilk: () => ESTIMATE_CONSTANT["シルク追加10個以上（部品番号・ピン番除く）"].なし.value,
  impedance: () => ESTIMATE_CONSTANT.特性インピーダンスコントロール.なし.value,

  resistPrint({ kibanType, layer }: Pick<EstimateValues, "kibanType" | "layer">) {
    if (isRigid({ kibanType }))
      return getNumberOfLayers({ layer }) === 1
        ? ESTIMATE_CONSTANT.塗布する面["パターン面と同じ面に塗る(L1面に塗る)"].value
        : ESTIMATE_CONSTANT.塗布する面.両面に塗る.value
    if (isFlexible({ kibanType })) return ""
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT.塗布する面["パターン面と同じ面に塗る(L1面に塗る)"].value

    throw new Error(createMessage({ kibanType, layer }))
  },
  resistColor({ kibanType }: Pick<EstimateValues, "kibanType">) {
    if (isRigid({ kibanType })) return ESTIMATE_CONSTANT.レジスト色["緑(リジッド)"].value
    if (isFlexible({ kibanType })) return ""
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT.レジスト色["白(メタル)"].value

    throw new Error(createMessage({ kibanType }))
  },

  coverLaySide({ layer }: Pick<EstimateValues, "layer">) {
    return getNumberOfLayers({ layer }) === 1
      ? ESTIMATE_CONSTANT.貼る面["パターン面と同じ面を貼る(L1面を貼る)"].value
      : ESTIMATE_CONSTANT.貼る面.両面貼る.value
  },
  coverLay: () => ESTIMATE_CONSTANT.カバーレイ["12.5μm"].value,
  coverLayColor: () => ESTIMATE_CONSTANT.カバーレイ色.標準色.value,

  silk({ kibanType, layer }: Pick<EstimateValues, "kibanType" | "layer">) {
    if (isRigid({ kibanType }))
      return getNumberOfLayers({ layer }) === 1
        ? ESTIMATE_CONSTANT.印刷する面["パターン面とは逆の面(Ln面)"].value
        : ESTIMATE_CONSTANT.印刷する面["パターン面と同じ面(L1面)"].value
    if (isFlexible({ kibanType })) return ESTIMATE_CONSTANT.印刷する面.なし.value
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT.印刷する面["パターン面と同じ面(L1面)"].value

    throw new Error(createMessage({ kibanType, layer }))
  },
  silkColor({ kibanType }: Pick<EstimateValues, "kibanType">) {
    if (isRigid({ kibanType })) return ESTIMATE_CONSTANT.シルク印刷色["白(リジッド)"].value
    if (isFlexible({ kibanType })) return ESTIMATE_CONSTANT.シルク印刷色["白(フレキシブル)"].value
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT.シルク印刷色["黒(メタル)"].value

    throw new Error(createMessage({ kibanType }))
  },
  silkPrintMethod: () => ESTIMATE_CONSTANT.シルク印刷工法.お任せ.value,

  surfaceFinishing({ kibanType }: Pick<EstimateValues, "kibanType">) {
    if (isRigid({ kibanType })) return ESTIMATE_CONSTANT.表面処理["水溶性フラックス(リジッド)"].value
    if (isFlexible({ kibanType })) return ESTIMATE_CONSTANT.表面処理["無電解金フラッシュ(フレキシブル)"].value
    if (isMetal({ kibanType })) return ESTIMATE_CONSTANT.表面処理["水溶性フラックス(メタル)"].value

    throw new Error(createMessage({ kibanType }))
  },
  hardGoldOnPCIEdgeTerminal: () => ESTIMATE_CONSTANT.端子部のみ電解金めっき.しない.value,
  chamferingProcess({ hardGoldOnPCIEdgeTerminal }: Pick<EstimateValues, "hardGoldOnPCIEdgeTerminal">) {
    if (hardGoldOnPCIEdgeTerminal === ESTIMATE_CONSTANT.端子部のみ電解金めっき.する.value) {
      return ESTIMATE_CONSTANT.端子部の面取り加工.角度指定無し.value
    }
    if (hardGoldOnPCIEdgeTerminal === ESTIMATE_CONSTANT.端子部のみ電解金めっき.しない.value) {
      return ESTIMATE_CONSTANT.端子部の面取り加工.しない.value
    }

    throw new Error(createMessage({ hardGoldOnPCIEdgeTerminal }))
  },

  rfContactPortionCnt: () => ESTIMATE_CONSTANT.端子部厚み指定.なし.value,
  rfContactPortion1: () => ESTIMATE_CONSTANT.仕上り厚み指定["0.2±0.03mm"].value,
  rfContactPortionBonding1: get接着面,
  rfContactPortion2: () => ESTIMATE_CONSTANT.仕上り厚み指定["0.2±0.03mm"].value,
  rfContactPortionBonding2: get接着面,
  reinForcementCnt: () => ESTIMATE_CONSTANT.補強板厚み指定.なし.value,
  reinForcement1: () => ESTIMATE_CONSTANT.厚み指定["0.1mmPI"].value,
  reinForcementBonding1: get接着面,
  reinForcement2: () => ESTIMATE_CONSTANT.厚み指定["0.1mmPI"].value,
  reinForcementBonding2: get接着面,

  panelizedItemCnt: () => 1,
  panelizedItemKindCnt: () => 1,
  fpcSurfacePutData: () => ESTIMATE_CONSTANT.FPC面付けされたデータ.なし.value,
  vcutCnt: () => null,
  jumpVcutCnt: () => null,
  routerCut: () => ESTIMATE_CONSTANT["ルーター切り出し面付け 面付け数"].なし.value,
  routerCutKind: () => null,
  roulette: () => ESTIMATE_CONSTANT["ミシン目面付け 面付け数"].なし.value,
  rouletteKind: () => null,
  dataService: () => ESTIMATE_CONSTANT.データ面付け編集サービス.なし.value,
  discardBoardMark: () => false,
  recognizeMark: () => false,
  editVariantSlit: () => false,

  semiTh: () => ESTIMATE_CONSTANT.端面スルーホール.なし.value,
  longHole: () => ESTIMATE_CONSTANT.長穴.なし.value,
  padOnVia: () => ESTIMATE_CONSTANT["パッド・オン・ビア"].なし.value,
  padLess03: () => ESTIMATE_CONSTANT["0.3mm未満のパッド"].なし.value,
  emcShield({ kibanType, layer }: Pick<EstimateValues, "kibanType" | "layer">) {
    if (!isFlexible({ kibanType })) {
      return EMPTY
    }

    switch (getNumberOfLayers({ layer })) {
      case 1:
        return ESTIMATE_CONSTANT.電磁波シールド["貼らない(片面)"].value
      case 2:
        return ESTIMATE_CONSTANT.電磁波シールド["貼らない(2層)"].value
      case 3:
        return ESTIMATE_CONSTANT.電磁波シールド["貼らない(3層)"].value
      case 4:
        return ESTIMATE_CONSTANT.電磁波シールド["貼らない(4層)"].value
    }

    throw new Error(createMessage({ kibanType, layer }))
  },

  ulMark: () => ESTIMATE_CONSTANT.ULマーク.なし.value,

  aoiInspection: () => ESTIMATE_CONSTANT["AOI検査（外観光学検査）"].なし.value,
  manufactureInspectionCertificate: () => ESTIMATE_CONSTANT.製造検査成績書.発行なし.value,
  mountInspectionCertificate: () => ESTIMATE_CONSTANT.実装検査成績書.発行なし.value,

  dxfData: () => ESTIMATE_CONSTANT.データ形式["ガーバーデータ 又はCADLUS Xデータ（.comp形式）"].value,
  manuOptPlanCad: () => ESTIMATE_CONSTANT.設計CAD.指定なし.value,

  estManuFactoryId: () => ESTIMATE_CONSTANT.製造工場.指定なし.value,
  rigidFlexPcb: () => ESTIMATE_CONSTANT.リジッドフレキ基板.なし.value,
  wireBonding: () => ESTIMATE_CONSTANT.ワイヤーボンディング.なし.value,
  awDataSupplied: () => ESTIMATE_CONSTANT["AWデータ支給設計（CAD／LIB必須）"].なし.value,
  panelized: () => ESTIMATE_CONSTANT.面付け.なし.value,
  ddrEmc: () => ESTIMATE_CONSTANT["DDR I／F・EMC設計（伝送線路シミュレーション等）"].なし.value,
  designDaysShortening: () => ESTIMATE_CONSTANT.納期短縮対応.なし.value,
  dxfDataOutput: () => ESTIMATE_CONSTANT.DXFデータ出力.なし.value,
} as const

export default getInitialValue
